import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RecentSearches = ({ onStockSelect, onSearchSelect }) => {
  const [recentSearches, setRecentSearches] = useState([]);
  const [popularStocks, setPopularStocks] = useState([]);

  // Mock recent searches
  const mockRecentSearches = [
    'AAPL', 'GOOGL', 'TSLA', 'MSFT', 'AMZN'
  ];

  // Mock popular stocks
  const mockPopularStocks = [
    {
      symbol: 'AAPL',
      name: 'Apple Inc.',
      price: 189.25,
      change: 2.45,
      changePercent: 1.31,
      logo: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=100&h=100&fit=crop&crop=center'
    },
    {
      symbol: 'NVDA',
      name: 'NVIDIA Corporation',
      price: 456.78,
      change: 18.45,
      changePercent: 4.21,
      logo: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=100&h=100&fit=crop&crop=center'
    },
    {
      symbol: 'TSLA',
      name: 'Tesla Inc.',
      price: 245.60,
      change: -8.90,
      changePercent: -3.50,
      logo: 'https://images.unsplash.com/photo-1560958089-b8a1929cea89?w=100&h=100&fit=crop&crop=center'
    },
    {
      symbol: 'MSFT',
      name: 'Microsoft Corporation',
      price: 378.90,
      change: 5.67,
      changePercent: 1.52,
      logo: 'https://images.unsplash.com/photo-1633419461186-7d40a38105ec?w=100&h=100&fit=crop&crop=center'
    }
  ];

  useEffect(() => {
    // Load recent searches from localStorage
    const saved = localStorage.getItem('recentStockSearches');
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    } else {
      setRecentSearches(mockRecentSearches);
    }
    
    setPopularStocks(mockPopularStocks);
  }, []);

  const handleClearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recentStockSearches');
  };

  const handleRemoveSearch = (searchTerm) => {
    const updated = recentSearches.filter(term => term !== searchTerm);
    setRecentSearches(updated);
    localStorage.setItem('recentStockSearches', JSON.stringify(updated));
  };

  return (
    <div className="space-y-8">
      {/* Recent Searches */}
      {recentSearches.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground">Recent Searches</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearRecentSearches}
              className="text-muted-foreground hover:text-foreground"
            >
              Clear All
            </Button>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {recentSearches.map((search) => (
              <div
                key={search}
                className="flex items-center bg-muted/50 rounded-full px-3 py-2 hover:bg-muted transition-colors"
              >
                <button
                  onClick={() => onSearchSelect(search)}
                  className="text-sm font-medium text-foreground hover:text-primary transition-colors"
                >
                  {search}
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveSearch(search)}
                  className="h-4 w-4 ml-2 hover:bg-destructive/20 hover:text-destructive"
                >
                  <Icon name="X" size={12} />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Popular Stocks */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">Popular Stocks</h2>
          <Button variant="ghost" size="sm">
            View All
            <Icon name="ArrowRight" size={16} className="ml-2" />
          </Button>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {popularStocks.map((stock) => (
            <div
              key={stock.symbol}
              onClick={() => onStockSelect(stock)}
              className="bg-card border border-border rounded-lg p-4 cursor-pointer hover:shadow-hover transition-all duration-200 hover:-translate-y-1"
            >
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center overflow-hidden">
                  <img
                    src={stock.logo}
                    alt={stock.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm" style={{ display: 'none' }}>
                    {stock.symbol.charAt(0)}
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">{stock.symbol}</h3>
                  <p className="text-xs text-muted-foreground truncate max-w-24">{stock.name}</p>
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold font-mono text-foreground">
                    ${stock.price.toFixed(2)}
                  </span>
                  <div className={`text-right ${stock.change >= 0 ? 'text-success' : 'text-destructive'}`}>
                    <div className="font-mono text-xs">
                      {stock.change >= 0 ? '+' : ''}{stock.change.toFixed(2)}
                    </div>
                    <div className="font-mono text-xs">
                      ({stock.change >= 0 ? '+' : ''}{stock.changePercent.toFixed(2)}%)
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Market Indices */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Market Indices</h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { name: 'S&P 500', symbol: 'SPX', value: 4567.89, change: 23.45, changePercent: 0.52 },
            { name: 'Dow Jones', symbol: 'DJI', value: 34567.12, change: -45.67, changePercent: -0.13 },
            { name: 'NASDAQ', symbol: 'IXIC', value: 14234.56, change: 67.89, changePercent: 0.48 }
          ].map((index) => (
            <div
              key={index.symbol}
              className="bg-card border border-border rounded-lg p-4"
            >
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-foreground">{index.name}</h3>
                  <span className="text-xs text-muted-foreground">{index.symbol}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold font-mono text-foreground">
                    {index.value.toLocaleString()}
                  </span>
                  <div className={`text-right ${index.change >= 0 ? 'text-success' : 'text-destructive'}`}>
                    <div className="font-mono text-sm">
                      {index.change >= 0 ? '+' : ''}{index.change.toFixed(2)}
                    </div>
                    <div className="font-mono text-xs">
                      ({index.change >= 0 ? '+' : ''}{index.changePercent.toFixed(2)}%)
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RecentSearches;